import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-zinc-900">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20 dark:bg-zinc-900">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <img src="/images/logo.png" alt="Nerava" className="w-8 h-8" />
            <span className="font-bold text-slate-900 dark:text-zinc-50">Nerava</span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-slate-900 mb-2 dark:text-zinc-50" style={{ fontFamily: 'Manrope' }}>Syarat & Ketentuan</h1>
        <p className="text-slate-500 mb-8 dark:text-zinc-400">Terakhir diperbarui: 1 Januari 2025</p>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 dark:text-zinc-50">1. Penerimaan Syarat</h2>
            <p className="text-slate-600">
              Dengan mengakses atau menggunakan layanan Nerava.id ("Layanan"), Anda menyetujui untuk 
              terikat oleh Syarat & Ketentuan ini ("Syarat"). Jika Anda tidak menyetujui Syarat ini, 
              Anda tidak diperkenankan menggunakan Layanan kami. Syarat ini merupakan perjanjian hukum 
              yang mengikat antara Anda dan Nerava.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 dark:text-zinc-50">2. Definisi</h2>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li><strong>"Nerava"</strong> merujuk pada platform manajemen bisnis berbasis AI yang dioperasikan oleh Nerava.id</li>
              <li><strong>"Pengguna"</strong> merujuk pada individu atau entitas bisnis yang mendaftar dan menggunakan Layanan</li>
              <li><strong>"AI Agent"</strong> merujuk pada asisten virtual berbasis kecerdasan buatan yang dibuat melalui platform kami untuk membalas pesan pelanggan secara otomatis</li>
              <li><strong>"Channel"</strong> merujuk pada saluran komunikasi yang didukung, termasuk WhatsApp, Instagram Direct Message, dan Facebook Messenger</li>
              <li><strong>"Konten"</strong> merujuk pada semua data, informasi produk, katalog, pesan, dan konfigurasi yang Anda unggah atau simpan di platform</li>
              <li><strong>"Langganan"</strong> merujuk pada paket berbayar yang Anda pilih untuk mengakses fitur premium Nerava</li>
              <li><strong>"Pelanggan"</strong> merujuk pada pihak ketiga yang berkomunikasi dengan bisnis Anda melalui Channel yang terhubung ke Nerava</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 dark:text-zinc-50">3. Pendaftaran Akun</h2>
            <p className="text-slate-600 mb-4">Untuk menggunakan Layanan, Anda harus:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>Berusia minimal 18 tahun atau memiliki persetujuan orang tua/wali yang sah</li>
              <li>Merupakan pemilik atau perwakilan resmi dari bisnis yang didaftarkan</li>
              <li>Memberikan informasi yang akurat, terkini, dan lengkap saat pendaftaran</li>
              <li>Menjaga kerahasiaan kredensial akun Anda (email dan kata sandi)</li>
              <li>Bertanggung jawab penuh atas semua aktivitas yang terjadi di akun Anda</li>
              <li>Segera memberitahu kami jika ada penggunaan tidak sah atas akun Anda</li>
              <li>Tidak membuat lebih dari satu akun untuk satu bisnis yang sama</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 dark:text-zinc-50">4. Layanan yang Disediakan</h2>
            <p className="text-slate-600 mb-4">Nerava menyediakan platform manajemen bisnis untuk UMKM Indonesia yang mencakup:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>AI Auto-Reply untuk WhatsApp Business, Instagram DM, dan Facebook Messenger</li>
              <li>Unified Inbox untuk mengelola semua percakapan pelanggan dalam satu dashboard</li>
              <li>Manajemen pesanan otomatis dari percakapan chat</li>
              <li>Katalog produk digital dengan informasi harga, stok, dan varian</li>
              <li>Kalkulasi ongkos kirim otomatis menggunakan data ekspedisi di Indonesia</li>
              <li>CRM (Customer Relationship Management) untuk mengelola data pelanggan</li>
              <li>Analitik dan insight bisnis berbasis AI</li>
              <li>Sistem follow-up otomatis untuk meningkatkan konversi penjualan</li>
              <li>Integrasi pembayaran dan pembuatan invoice</li>
              <li>Booking pengiriman otomatis melalui mitra logistik</li>
            </ul>
            <p className="text-slate-600 mt-4">
              Kami berhak untuk memodifikasi, memperbarui, menambahkan, atau menghapus fitur apa pun 
              dari Layanan dengan atau tanpa pemberitahuan sebelumnya, sesuai dengan kebutuhan pengembangan platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 dark:text-zinc-50">5. Paket Layanan dan Pembayaran</h2>
            <h3 className="text-lg font-medium text-slate-800 mb-2 dark:text-zinc-50">5.1 Paket Trial (Gratis)</h3>
            <p className="text-slate-600 mb-4">
              Setiap pengguna baru mendapatkan akses trial gratis selama 30 hari dengan akses penuh ke seluruh fitur. 
              Setelah masa trial berakhir, pengguna harus memilih paket berbayar untuk melanjutkan penggunaan.
            </p>
            
            <h3 className="text-lg font-medium text-slate-800 mb-2 dark:text-zinc-50">5.2 Paket Berbayar</h3>
            <p className="text-slate-600 mb-4">
              Nerava menyediakan beberapa paket berbayar: Starter (Rp 199.000/bulan), Growth (Rp 449.000/bulan), 
              Business (Rp 999.000/bulan), dan Enterprise (harga khusus). Setiap paket memiliki batasan jumlah 
              AI Agent, kuota pesan, dan fitur yang berbeda sesuai dengan yang tercantum di halaman harga.
            </p>
            
            <h3 className="text-lg font-medium text-slate-800 mb-2 dark:text-zinc-50">5.3 Penagihan dan Pembayaran</h3>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>Langganan ditagih di muka secara bulanan</li>
              <li>Pembayaran dilakukan melalui penyedia pembayaran pihak ketiga yang telah diverifikasi (Duitku)</li>
              <li>Semua harga yang tercantum belum termasuk pajak yang berlaku</li>
              <li>Kegagalan pembayaran dalam jangka waktu 7 hari dari tanggal jatuh tempo dapat mengakibatkan penangguhan layanan</li>
            </ul>

            <h3 className="text-lg font-medium text-slate-800 mb-2 mt-4 dark:text-zinc-50">5.4 Kebijakan Pengembalian Dana</h3>
            <p className="text-slate-600">
              Biaya langganan yang telah dibayarkan tidak dapat dikembalikan (non-refundable), kecuali dalam kondisi berikut:
              (a) Layanan tidak tersedia selama lebih dari 72 jam berturut-turut karena kesalahan kami;
              (b) Terjadi penagihan ganda yang dibuktikan dengan bukti pembayaran yang sah.
              Permintaan pengembalian dana harus diajukan dalam waktu 14 hari sejak kejadian melalui email ke support@nerava.id.
            </p>

            <h3 className="text-lg font-medium text-slate-800 mb-2 mt-4 dark:text-zinc-50">5.5 Upgrade dan Downgrade Paket</h3>
            <p className="text-slate-600">
              Pengguna dapat mengubah paket langganan kapan saja. Upgrade berlaku segera setelah pembayaran berhasil. 
              Downgrade akan berlaku di akhir periode billing yang sedang berjalan. Saat downgrade, jumlah AI Agent 
              yang melebihi batas paket baru akan dinonaktifkan secara otomatis.
            </p>
            <h3 className="text-lg font-medium text-slate-800 mb-2 mt-4 dark:text-zinc-50">5.6 Biaya Pihak Ketiga di Luar Langganan</h3>
            <p className="text-slate-600">
              Harga paket Nerava tidak mencakup biaya yang ditagihkan langsung oleh pihak ketiga kepada Pengguna.
              Khususnya, apabila Pengguna menghubungkan nomor WhatsApp Business miliknya sendiri, Meta menagih
              biaya pesan layanan (service message) langsung ke akun WhatsApp Business Pengguna setelah melewati
              kuota pesan gratis bulanan yang ditetapkan Meta. Besaran dan kuota tersebut ditentukan sepenuhnya
              oleh Meta dan dapat berubah sewaktu-waktu. Percakapan melalui Instagram, Messenger, dan live chat
              website tidak dikenakan biaya pesan oleh Meta.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 dark:text-zinc-50">6. Penggunaan yang Diizinkan dan Dilarang</h2>
            <p className="text-slate-600 mb-4">Anda setuju untuk menggunakan Layanan hanya untuk tujuan bisnis yang sah dan sesuai hukum. Anda <strong>TIDAK BOLEH</strong>:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>Menggunakan Layanan untuk tujuan yang melanggar hukum Republik Indonesia atau hukum internasional</li>
              <li>Menggunakan AI Agent untuk menyebarkan informasi palsu, menyesatkan, atau memfitnah</li>
              <li>Menggunakan Layanan untuk spam, phishing, penipuan, atau social engineering</li>
              <li>Mencoba mengakses akun, sistem, atau data pengguna lain tanpa izin</li>
              <li>Mengganggu, merusak, atau membebani infrastruktur dan server Layanan</li>
              <li>Menjual kembali, menyewakan, atau mendistribusikan akses ke Layanan kepada pihak ketiga tanpa izin tertulis</li>
              <li>Menggunakan Layanan untuk mengumpulkan data pribadi pelanggan secara ilegal atau tanpa persetujuan</li>
              <li>Membuat konten AI Agent yang mengandung ujaran kebencian, SARA, pornografi, atau konten ilegal lainnya</li>
              <li>Memanipulasi atau menyalahgunakan sistem pembayaran, diskon, atau voucher</li>
              <li>Menggunakan Layanan untuk menjual produk atau jasa yang dilarang oleh hukum</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 dark:text-zinc-50">7. Integrasi Pihak Ketiga</h2>
            <p className="text-slate-600 mb-4">
              Nerava terintegrasi dengan layanan pihak ketiga termasuk namun tidak terbatas pada:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li><strong>Meta Platform (WhatsApp, Instagram, Messenger)</strong> — Untuk menerima dan mengirim pesan pelanggan. Penggunaan tunduk pada kebijakan Meta Platform.</li>
              <li><strong>Google (Gemini AI)</strong> — Untuk pemrosesan bahasa alami dan respons AI. Data percakapan diproses sesuai kebijakan privasi Google.</li>
              <li><strong>Komerce/RajaOngkir</strong> — Untuk kalkulasi ongkos kirim dan booking pengiriman.</li>
              <li><strong>Duitku</strong> — Untuk pemrosesan pembayaran langganan.</li>
              <li><strong>HERE Technologies</strong> — Untuk validasi dan geocoding alamat pengiriman.</li>
              <li><strong>ImgBB</strong> — Untuk penyimpanan gambar produk.</li>
              <li><strong>Resend</strong> — Untuk pengiriman email notifikasi dan invoice.</li>
            </ul>
            <p className="text-slate-600 mt-4">
              Kami tidak bertanggung jawab atas ketersediaan, kualitas, atau perubahan kebijakan dari layanan pihak ketiga tersebut.
              Gangguan pada layanan pihak ketiga dapat mempengaruhi fungsionalitas Nerava.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 dark:text-zinc-50">8. Konten dan Data Pengguna</h2>
            <p className="text-slate-600 mb-4">
              Anda mempertahankan kepemilikan penuh atas semua Konten yang Anda unggah atau simpan di platform, termasuk 
              data produk, informasi pelanggan, dan konfigurasi AI Agent. Dengan menggunakan Layanan, Anda memberikan 
              Nerava lisensi non-eksklusif, terbatas, untuk menyimpan, memproses, dan menampilkan Konten tersebut 
              semata-mata untuk tujuan penyediaan Layanan.
            </p>
            <p className="text-slate-600 mb-4">
              Anda bertanggung jawab penuh atas:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>Keakuratan informasi produk (harga, stok, deskripsi) yang dimasukkan ke dalam katalog</li>
              <li>Kepatuhan terhadap peraturan perlindungan data pelanggan yang berlaku</li>
              <li>Persetujuan pelanggan Anda untuk pemrosesan data melalui AI Agent</li>
              <li>Backup data penting secara mandiri (meskipun Nerava melakukan backup rutin)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 dark:text-zinc-50">9. Kekayaan Intelektual</h2>
            <p className="text-slate-600">
              Platform Nerava, termasuk desain antarmuka, kode sumber, algoritma AI, fitur, logo, dan seluruh 
              dokumentasi, adalah milik eksklusif Nerava dan dilindungi oleh hukum kekayaan intelektual Republik 
              Indonesia serta hukum internasional. Anda tidak boleh menyalin, memodifikasi, mendistribusikan, 
              merekayasa balik, atau membuat karya turunan dari Layanan tanpa izin tertulis dari Nerava.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 dark:text-zinc-50">10. Ketersediaan Layanan</h2>
            <p className="text-slate-600 mb-4">
              Kami berupaya menjaga Layanan tetap tersedia dan memantau gangguan secara aktif, namun paket
              mandiri (Trial, Starter, Growth, Business) tidak disertai jaminan uptime tertulis dan kami
              tidak memublikasikan angka ketersediaan. Jaminan tingkat layanan tertulis hanya tersedia
              melalui perjanjian Enterprise. Layanan dapat mengalami gangguan karena:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>Pemeliharaan terjadwal (akan diinformasikan minimal 24 jam sebelumnya)</li>
              <li>Pembaruan dan perbaikan darurat</li>
              <li>Gangguan pada layanan pihak ketiga (Meta, Google, dll.)</li>
              <li>Force majeure (bencana alam, gangguan internet nasional, dll.)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 dark:text-zinc-50">11. Batasan Tanggung Jawab</h2>
            <p className="text-slate-600 mb-4">
              LAYANAN DISEDIAKAN "SEBAGAIMANA ADANYA" (AS IS) DAN "SEBAGAIMANA TERSEDIA" (AS AVAILABLE) TANPA 
              JAMINAN APA PUN, BAIK TERSURAT MAUPUN TERSIRAT. NERAVA TIDAK BERTANGGUNG JAWAB ATAS:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>Kehilangan data, keuntungan, atau pendapatan bisnis</li>
              <li>Gangguan atau penundaan dalam operasional bisnis Anda</li>
              <li>Kerusakan tidak langsung, insidental, khusus, atau konsekuensial</li>
              <li>Ketidakakuratan respons AI Agent, termasuk informasi harga atau produk yang salah</li>
              <li>Tindakan atau kelalaian pelanggan Anda berdasarkan informasi dari AI Agent</li>
              <li>Kegagalan pengiriman pesan karena gangguan platform pihak ketiga (WhatsApp, Instagram, dll.)</li>
              <li>Transaksi atau pembayaran yang terjadi antara Anda dan pelanggan Anda</li>
            </ul>
            <p className="text-slate-600 mt-4">
              Total tanggung jawab kumulatif Nerava tidak akan melebihi jumlah total yang telah Anda bayarkan 
              kepada Nerava dalam 12 (dua belas) bulan terakhir sebelum klaim diajukan.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 dark:text-zinc-50">12. Ganti Rugi</h2>
            <p className="text-slate-600">
              Anda setuju untuk membela, mengganti rugi, dan membebaskan Nerava, direksi, karyawan, dan afiliasinya 
              dari segala klaim, tuntutan, kerugian, denda, atau biaya (termasuk biaya pengacara yang wajar) yang 
              timbul dari atau terkait dengan: (a) penggunaan Anda atas Layanan; (b) pelanggaran Anda terhadap 
              Syarat ini; (c) pelanggaran Anda terhadap hak pihak ketiga; atau (d) Konten yang Anda unggah atau 
              buat melalui platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 dark:text-zinc-50">13. Penangguhan dan Penghentian Akun</h2>
            <p className="text-slate-600 mb-4">
              Kami berhak untuk menangguhkan atau menghentikan akun Anda jika:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>Anda melanggar ketentuan dalam Syarat ini</li>
              <li>Anda tidak membayar biaya langganan yang jatuh tempo dalam waktu 14 hari</li>
              <li>Terdapat indikasi penyalahgunaan atau aktivitas mencurigakan pada akun Anda</li>
              <li>Kami diwajibkan oleh hukum, perintah pengadilan, atau otoritas yang berwenang</li>
              <li>Akun Anda tidak aktif selama lebih dari 12 bulan berturut-turut</li>
            </ul>
            <p className="text-slate-600 mt-4">
              Anda dapat menghentikan akun Anda kapan saja melalui pengaturan akun atau dengan menghubungi 
              support@nerava.id. Setelah penghentian, data Anda akan disimpan selama 30 hari sebelum dihapus 
              secara permanen, kecuali diwajibkan lain oleh hukum.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 dark:text-zinc-50">14. Privasi dan Perlindungan Data</h2>
            <p className="text-slate-600">
              Pengumpulan, penggunaan, dan perlindungan data pribadi Anda diatur dalam Kebijakan Privasi kami 
              yang merupakan bagian tidak terpisahkan dari Syarat ini. Silakan baca {' '}
              <a href="/privacy-policy" className="text-indigo-600 hover:underline dark:text-indigo-400">Kebijakan Privasi</a> kami 
              untuk informasi lebih lanjut tentang bagaimana kami mengelola data Anda sesuai dengan Undang-Undang 
              Perlindungan Data Pribadi (UU PDP) Republik Indonesia.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 dark:text-zinc-50">15. Perubahan Syarat</h2>
            <p className="text-slate-600">
              Kami dapat merevisi Syarat ini dari waktu ke waktu sesuai dengan perkembangan layanan dan peraturan 
              yang berlaku. Perubahan material akan diinformasikan melalui email atau notifikasi di platform 
              minimal 14 hari sebelum berlaku. Penggunaan berkelanjutan atas Layanan setelah perubahan berlaku 
              merupakan penerimaan atas Syarat yang direvisi.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 dark:text-zinc-50">16. Hukum yang Berlaku dan Penyelesaian Sengketa</h2>
            <p className="text-slate-600">
              Syarat ini diatur oleh dan ditafsirkan sesuai dengan hukum Republik Indonesia. Setiap sengketa yang 
              timbul dari atau terkait dengan Syarat ini akan diselesaikan melalui: (a) musyawarah untuk mufakat 
              dalam waktu 30 hari; (b) jika tidak tercapai, melalui mediasi di bawah Badan Arbitrase Nasional 
              Indonesia (BANI); (c) jika mediasi gagal, melalui pengadilan negeri yang berwenang di Jakarta, Indonesia.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 dark:text-zinc-50">17. Ketentuan Umum</h2>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li><strong>Keterpisahan:</strong> Jika ada ketentuan dalam Syarat ini yang dinyatakan tidak sah atau tidak dapat dilaksanakan oleh pengadilan, ketentuan lainnya tetap berlaku sepenuhnya.</li>
              <li><strong>Pelepasan Hak:</strong> Kegagalan Nerava untuk menegakkan hak atau ketentuan apa pun dalam Syarat ini tidak merupakan pelepasan hak tersebut.</li>
              <li><strong>Keseluruhan Perjanjian:</strong> Syarat ini, bersama dengan Kebijakan Privasi, merupakan keseluruhan perjanjian antara Anda dan Nerava terkait penggunaan Layanan.</li>
              <li><strong>Pengalihan:</strong> Anda tidak boleh mengalihkan hak atau kewajiban berdasarkan Syarat ini tanpa persetujuan tertulis dari Nerava.</li>
              <li><strong>Force Majeure:</strong> Nerava tidak bertanggung jawab atas kegagalan atau keterlambatan dalam memenuhi kewajiban yang disebabkan oleh keadaan di luar kendali wajar, termasuk bencana alam, pandemi, perang, gangguan internet, atau tindakan pemerintah.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 dark:text-zinc-50">18. Kontak</h2>
            <p className="text-slate-600 mb-4">
              Untuk pertanyaan, keluhan, atau permintaan terkait Syarat & Ketentuan ini, silakan hubungi kami melalui:
            </p>
            <div className="bg-slate-100 rounded-xl p-6 mt-4 dark:bg-zinc-800">
              <p className="text-slate-700 font-semibold text-lg mb-2 dark:text-zinc-200">Nerava.id</p>
              <p className="text-slate-600">Email: support@nerava.id</p>
              <p className="text-slate-600">WhatsApp: +62 851-1144-4413</p>
              <p className="text-slate-600">Website: https://nerava.id</p>
              <p className="text-slate-500 text-sm mt-3 dark:text-zinc-400">Waktu respons: Maksimal 2x24 jam pada hari kerja</p>
            </div>
          </section>
        </div>

        <div className="border-t border-slate-200 mt-12 pt-8 text-center">
          <p className="text-slate-500 text-sm dark:text-zinc-400">© 2026 Nerava.id. All rights reserved.</p>
        </div>
      </main>
    </div>
  );
};

export default TermsConditions;
